import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AuditEntry } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Button } from "@/ui/Button";
import { Choice } from "@/ui/Choice";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { downloadCSV } from "@/features/admin/csv";
import { absoluteTime, humaniseAction, shortTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/staff")({
  component: StaffActionsScreen,
});

/**
 * What people from Drumlin did, separate from what customers did.
 *
 * # WHY IT IS ITS OWN SCREEN
 *
 * The platform activity log is everything everyone did, which at any volume is
 * overwhelmingly customers deploying their own services. The question an
 * operator has — what did my colleagues do to customer accounts today — was
 * answerable only by scrolling past all of it.
 *
 * Narrowed by the action's name rather than by a list of actions. Every staff
 * action is called admin.something, so one added next year appears here without
 * anybody remembering to add it — and a screen whose purpose is to show what
 * staff did is exactly the screen that must not quietly miss one.
 */
function StaffActionsScreen() {
  const [actor, setActor] = useState("");

  const entries = useQuery({
    queryKey: ["staff-actions", actor],
    queryFn: ({ signal }) =>
      api.get<{ entries: AuditEntry[] | null }>(
        "/admin/audit-log",
        { staff_only: true, actor_id: actor || undefined, limit: 200 },
        signal,
      ),
  });

  const list = entries.data?.entries ?? [];
  // Who has been acting, so the filter is a click rather than an identifier
  // somebody has to find first.
  const actors = [...new Map(list.map((e) => [e.actor_id, e.actor_label || e.actor_id])).entries()]
    .filter(([id]) => id)
    .slice(0, 8);

  return (
    <Panel
      /* The filter leads and the export trails, which is the shape every
         other list on this surface has.
         # AND IT IS THE PRODUCT'S SELECT NOW
         It was a bare <select> with a handful of utility classes — the
         browser's control, the browser's arrow, the browser's option list,
         on a surface where every other dropdown is the same component. One
         screen rendering the operating system's widget is the kind of thing
         nobody files and everybody sees. */
      toolbar={
        actors.length > 1 ? (
          <div className="w-56">
            <Choice
              value={actor}
              onValueChange={setActor}
              aria-label="Filter by person"
              options={[
                { value: "", label: "Everybody" },
                ...actors.map(([id, label]) => ({ value: id ?? "", label: label ?? id ?? "" })),
              ]}
            />
          </div>
        ) : undefined
      }
      actions={
        <div className="flex items-center gap-2">
          <Button
            tone="secondary"
            size="dense"
            disabled={list.length === 0}
            onClick={() =>
              downloadCSV("drumlin-staff-actions", list, [
                { header: "when", value: (e) => e.created_at },
                { header: "who", value: (e) => e.actor_label || e.actor_id || "" },
                { header: "action", value: (e) => e.action },
                { header: "organisation", value: (e) => e.organisation_id ?? "" },
                { header: "target", value: (e) => e.target_label || e.target_type },
                { header: "target_id", value: (e) => e.target_id ?? "" },
                { header: "reason", value: (e) => e.reason ?? "" },
                { header: "outcome", value: (e) => e.outcome },
              ])
            }
          >
            Export
          </Button>
        </div>
      }
      padded={false}
    >
      {entries.isLoading ? (
        <div className="px-5 py-4">
          <TableSkeleton rows={8} columns={5} />
        </div>
      ) : entries.isError ? (
        <div className="px-5 py-4">
          <ErrorState error={entries.error} onRetry={() => void entries.refetch()} />
        </div>
      ) : list.length === 0 ? (
        <div className="px-5 py-8">
          <EmptyState
            title="Nothing yet"
            description="Nobody from Drumlin has touched a customer account."
          />
        </div>
      ) : (
        <div className="px-5 py-3">
          <Table dense fixed>
            <thead>
              <Tr>
                <Th width="w-[20%]">Who</Th>
                <Th width="w-[20%]">What</Th>
                <Th width="w-[18%]">On what</Th>
                <Th width="w-[28%]">Why</Th>
                <Th width="w-[14%]" align="right">
                  When
                </Th>
              </Tr>
            </thead>
            <tbody>
              {list.map((e) => (
                <Tr key={e.id}>
                  <Td clip title={e.actor_label || e.actor_id || e.actor_type}>
                    {e.actor_id ? (
                      <Link
                        to="/admin/users/$userId"
                        params={{ userId: e.actor_id }}
                        className="hover:text-[var(--primary)]"
                      >
                        {e.actor_label || e.actor_id}
                      </Link>
                    ) : (
                      e.actor_type
                    )}
                  </Td>
                  <Td clip title={e.action}>
                    {humaniseAction(e.action)}
                  </Td>
                  <Td clip title={e.target_id || undefined}>
                    {e.organisation_id ? (
                      <Link
                        to="/admin/$orgId"
                        params={{ orgId: e.organisation_id }}
                        className="hover:text-[var(--primary)]"
                      >
                        {e.target_label || e.target_type}
                      </Link>
                    ) : (
                      e.target_label || e.target_type
                    )}
                  </Td>
                  {/* Every staff action requires one now, so a blank here is
                      an entry from before that rule — worth seeing as blank
                      rather than hidden behind a dash that reads like nothing
                      was asked. */}
                  <Td clip title={e.reason || undefined}>
                    <span
                      className={
                        e.reason ? "text-[var(--text-secondary)]" : "text-[var(--text-muted)]"
                      }
                    >
                      {e.reason || "none recorded"}
                    </span>
                  </Td>
                  <Td align="right" title={absoluteTime(e.created_at)}>
                    {shortTime(e.created_at)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Panel>
  );
}
